import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import ListItemSkeletonWrapper from '@app/src/components/Skeleton/ListItem'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import SustainabilityLibraryCollection from '@app/src/pages/Configurations/SustainabilityLibraryPages/SustainabilityLibraryCollection'
import { SUSTAINABILITY_LIBRARY_PAGE_IDS } from '@app/src/pages/Configurations/SustainabilityLibraryScene'
import type {
  AssessmentLevelNamesType,
  AssessmentSection,
  QuestionnaireTemplate,
} from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { NotificationSeverity } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import type { RadioGroupProps } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { generatePath } from 'react-router'
import { useHistory } from 'react-router-dom'
import AssessmentForm from './AssessmentForm'
import { ASSESSMENT_INTERVALS } from './utils/constants'
import { transformQuestionnaireSections } from './utils/dataTransformers'

interface CreateAssessmentFromQuestionnaireProps {
  questionnaireTemplateId: number
}

export interface AssessmentTemplateFormValues {
  name: string
  description: string
  level1Description: string
  level2Description: string
  level3Description: string
  level4Description: string
  level5Description: string
  assessmentType: AssessmentType
  levelNamesType: AssessmentLevelNamesType
  sections?: AssessmentSection[]
}

const CreateAssessmentFromQuestionnaire: React.FC<CreateAssessmentFromQuestionnaireProps> = ({
  questionnaireTemplateId,
}) => {
  const { formatMessage } = useIntl()
  const [intervals, setIntervals] = useState<number>(ASSESSMENT_INTERVALS.DEFAULT)
  const formMethods = useForm<AssessmentTemplateFormValues>()
  const queryClient = useQueryClient()
  const { mutate: createAssessmentTemplate, isPending: isCreating } = useCreateResource()
  const { showErrorNotification } = useErrorNotification()
  const { showSnackbar } = useSnackbar()
  const history = useHistory()

  const { data: questionnaireTemplate } = useFetchResourceWithPost<QuestionnaireTemplate>({
    endpoint: endpoints.templateById(questionnaireTemplateId, true),
    body: [
      'creatorOrganization.name',
      'sections.questions.questionType',
      'sections.questions.unit',
      'sections.questions.requestCollection',
      'sections.questions.reportFlagRules',
      'sections.questions.reportFlagRules.creatorOrganization',
      'sections.questions.reportFlagRules.suggestedRiskGuidance.suggestedRiskNaceCodes.naceCode',
      'sections.questions.reportFlagRules.suggestedRiskGuidance.suggestedRiskGuidanceLinks',
      'sections.questions.questionAnswerClassificationRules',
      'sections.questions.options.category',
      'sections.questions.categorizations',
      'sections.sectionQuestions.questionnaireQuestionRules.sectionQuestionTargets',
      'sections.sectionQuestions.questionnaireQuestionRules.sectionQuestionTargets.targetQuestionSection',
    ],
    key: [FetchKey.OrganizationOwnedTemplates, questionnaireTemplateId],
    options: {
      enabled: Boolean(questionnaireTemplateId),
    },
  })

  const transformedSections = useMemo(() => {
    return questionnaireTemplate?.sections ? transformQuestionnaireSections(questionnaireTemplate.sections) : []
  }, [questionnaireTemplate?.sections])

  const handleIntervalsChange: RadioGroupProps['onChange'] = event => {
    setIntervals(parseInt(event.target.value))
  }

  const onSubmit = (values: AssessmentTemplateFormValues) => {
    if (!questionnaireTemplate) return

    const { sections, ...assessmentTemplateValues } = values
    const assessmentTemplate = {
      ...assessmentTemplateValues,
      questionnaireTemplateId: questionnaireTemplate.id,
      levels: intervals,
    }

    const questionIds = sections?.flatMap(s => s.questions.map(q => q.questionId)) ?? []

    let body
    if (values.assessmentType === AssessmentType.Flexible && sections) {
      body = {
        assessmentTemplate,
        sections: sections.map((section, i) => ({
          name: assessmentTemplateValues[`section${i + 1}Name` as keyof typeof assessmentTemplateValues],
          questions: section.questions.map(question => ({
            questionId: question.questionId,
            veryPositivePoints: question.points?.veryPositive || null,
            positivePoints: question.points?.positive || null,
            negativePoints: question.points?.negative || null,
            negativeAdversePoints: question.points?.negativeAdverse || null,
          })),
        })),
        questionIds,
        questionnaireTemplateId: questionnaireTemplate.id,
      }
    } else {
      body = {
        assessmentTemplate,
        questionIds,
        questionnaireTemplateId: questionnaireTemplate.id,
      }
    }

    createAssessmentTemplate(
      {
        url: endpoints.assessmentTemplate,
        body,
      },
      {
        onError: error => {
          showErrorNotification({ requestError: error })
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationSettings] })
          showSnackbar({
            message: formatMessage({ id: 'assessments.successfullyCreated' }),
            severity: NotificationSeverity.success,
          })
          history.push(
            generatePath(paths.sustainabilityLibrary, {
              configurationsPage: SUSTAINABILITY_LIBRARY_PAGE_IDS.Assessments,
            }),
          )
        },
      },
    )
  }

  return (
    <FormProvider {...formMethods}>
      <SustainabilityLibraryCollection
        title={formatMessage({ id: 'assessments.createAssessment' })}
        actionButtons={
          questionnaireTemplate
            ? [
                {
                  label: formatMessage({ id: 'general.cancel' }),
                  to: generatePath(paths.sustainabilityLibrary, {
                    configurationsPage: SUSTAINABILITY_LIBRARY_PAGE_IDS.Assessments,
                  }),
                  disabled: isCreating,
                },
                {
                  label: formatMessage({ id: 'resourceCollections.general.create' }),
                  onClick: formMethods.handleSubmit(onSubmit),
                  variant: 'contained',
                  disabled: isCreating,
                  loading: isCreating,
                },
              ]
            : undefined
        }
      >
        {!questionnaireTemplate ? (
          <ListItemSkeletonWrapper />
        ) : (
          <AssessmentForm
            intervals={intervals}
            onIntervalsChange={handleIntervalsChange}
            sections={transformedSections}
            questionnaireTemplateTitle={questionnaireTemplate.title}
          />
        )}
      </SustainabilityLibraryCollection>
    </FormProvider>
  )
}

export default CreateAssessmentFromQuestionnaire
